import React, { useState, useEffect } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";

import Toast from '../../components/Toast/Toast';
import ConfirmationModal from '../../components/modals/ConfirmationModal';
import ImageUploadModal from '../../components/ImageUploadModal/ImageUploadModal';
import { addProductAPI, getCategoriesAPI, getBrandsAPI, uploadImageAPI, deleteImageAPI } from '../../utils/api';
import Input from '../../components/Input/Input';
import TextArea from '../../components/Input/TextArea';
import Select from '../../components/Input/Select';
import Checkbox from '../../components/Input/Checkbox';
import "./AddProduct.css";
import { FaArrowLeft } from "react-icons/fa";
import RichTextInput from '../../components/Input/RichTextInput';

const getColorDistribution = (batch) => {
  const distribution = {};
  batch.forEach(item => {
    distribution[item.color] = (distribution[item.color] || 0) + 1;
  });
  return distribution;
};

const AddProduct = () => {
  const { catId } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const filter = searchParams.get('filter') || 'all';

  const [productName, setProductName] = useState("");
  const [description, setDescription] = useState("");
  const [featured, setFeatured] = useState(false);
  const [returnPolicy, setReturnPolicy] = useState("");
  const [fabric, setFabric] = useState("");
  const [categoryIds, setCategoryIds] = useState([]);
  const [BrandId, setBrandId] = useState("");
  const [colors, setColors] = useState([
    { name: "No Color", hex_code: "rgba(0, 0, 0, 0)", isNoColor: true }
  ]);
  const [sizes, setSizes] = useState([{ size: "", wholesale_price: "", retail_price: "", unit: "" }]);
  const [images, setImages] = useState([]);
  const [imageColors, setImageColors] = useState([]);
  const [uploadColor, setUploadColor] = useState("");
  const [categories, setCategories] = useState([]);
  const [brands, setBrands] = useState([]);
  const [loadingCategories, setLoadingCategories] = useState(false);
  const [loadingBrands, setLoadingBrands] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteColorData, setDeleteColorData] = useState(null);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [toast, setToast] = useState({ show: false, message: "", type: "" });
  const [productCreated, setProductCreated] = useState(false);
  const [createdProduct, setCreatedProduct] = useState(null);
  const [showImageUploadModal, setShowImageUploadModal] = useState(false);
  const [selectedColorForUpload, setSelectedColorForUpload] = useState(null);
  const [uploadingImages, setUploadingImages] = useState([]);
  const [showImageDeleteModal, setShowImageDeleteModal] = useState(false);
  const [imageToDelete, setImageToDelete] = useState(null);
  const showToast = (message, type = "error") => {
    setToast({ show: true, message, type });
    setTimeout(() => setToast({ show: false, message: "", type: "" }), 2500);
  };



  // Fetch categories from API
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        setLoadingCategories(true);
        const response = await getCategoriesAPI();

        if (response?.status === 200) {
          const data = response.data || [];
          setCategories(data);
        } else if (response?.message) {
          console?.error('Error loading categories:', typeof response?.message === 'string'
            ? response?.message
            : 'Failed due to internal error');
        } else {
          console?.error('Failed due to internal error');
        }
      } catch (err) {
        console?.error('Failed due to internal error');
      } finally {
        setLoadingCategories(false);
      }
    };

    fetchCategories();
  }, []);

  // Fetch brands from API
  useEffect(() => {
    const fetchBrands = async () => {
      try {
        setLoadingBrands(true);
        const response = await getBrandsAPI();

        if (response?.status === 200) {
          setBrands(response.data || []);
        } else if (response?.message) {
          console.error('Error loading brands:', typeof response?.message === 'string'
            ? response?.message
            : 'Failed due to internal error');
        } else {
          console.error('Failed due to internal error');
        }
      } catch (err) {
        console.error('Failed due to internal error');
      } finally {
        setLoadingBrands(false);
      }
    };

    fetchBrands();
  }, []);

  // Handle input change for color name or hex
  const handleColorChange = (idx, field, value) => {
    const updated = colors.map((color, i) =>
      i === idx ? { ...color, [field]: value } : color
    );
    setColors(updated);
  };

  // Add a new color input row
  const handleAddMoreColor = () => {
    setColors([...colors, { name: "", hex_code: "rgb(0, 0, 0)" }]);
  };

  const handleRemoveColor = (idx) => {
    const colorToDelete = colors[idx];

    // Check if this color has any images
    const colorImages = images.filter((img, idx) => {
      const imageColor = imageColors[idx] || "";
      return imageColor === colorToDelete.hex_code;
    });

    if (colorImages.length > 0) {
      // Show modal for confirmation
      setDeleteColorData({
        colorIdx: idx,
        colorName: colorToDelete.name,
        imageCount: colorImages.length
      });
      setShowDeleteModal(true);
    } else {
      // No images for this color, safe to delete
      const newColors = colors.filter((_, i) => i !== idx);
      setColors(newColors);
      showToast(`Color "${colorToDelete.name}" deleted successfully.`, "success");
    }
  };

  // Confirm color deletion from modal
  const confirmDeleteColor = () => {
    if (!deleteColorData) return;

    const { colorIdx, colorName, imageCount } = deleteColorData;
    const colorToDelete = colors[colorIdx];

    // Remove color and its associated images
    const newColors = colors.filter((_, i) => i !== colorIdx);
    const newImages = images.filter((img, idx) => {
      const imageColor = imageColors[idx] || "";
      return imageColor !== colorToDelete.hex_code;
    });
    const newImageColors = imageColors.filter((imgColor, idx) => {
      const imageColor = imageColors[idx] || "";
      return imageColor !== colorToDelete.hex_code;
    });

    setColors(newColors);
    setImages(newImages);
    setImageColors(newImageColors);

    showToast(`Color "${colorName}" and its ${imageCount} image(s) deleted successfully.`, "success");

    // Close modal
    setShowDeleteModal(false);
    setDeleteColorData(null);
  };

  // Cancel color deletion
  const cancelDeleteColor = () => {
    setShowDeleteModal(false);
    setDeleteColorData(null);
  };

  const handleSizeChange = (idx, field, value) => {
    // Only allow numbers for wholesale_price, retail_price, and unit
    if (["wholesale_price", "retail_price", "unit"].includes(field)) {
      if (!/^[0-9]*$/.test(value)) return;
    }
    const updated = sizes.map((row, i) =>
      i === idx ? { ...row, [field]: value } : row
    );
    setSizes(updated);
  };

  const handleAddSizeRow = () => {
    setSizes([...sizes, { size: "", wholesale_price: "", retail_price: "", unit: "" }]);
  };

  const handleRemoveSizeRow = (idx) => {
    setSizes(sizes.filter((_, i) => i !== idx));
  };

  // Convert file to base64
  const convertToBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader?.result);
      reader.onerror = error => reject(error);
    });
  };

  // Handle image upload button click
  const handleImageUpload = (colorHex) => {
    const color = createdProduct.colors.find(c => c.hex_code === colorHex);
    if (color) {
      setSelectedColorForUpload(color);
      setShowImageUploadModal(true);
    }
  };

  // Handle image selection in modal
  const handleModalImageChange = (e) => {
    const files = Array?.from(e?.target?.files);

    // Validate file types
    const allowedTypes = ['image/png', 'image/jpg', 'image/jpeg'];
    const invalidFiles = files.filter(file => !allowedTypes.includes(file.type));

    if (invalidFiles.length > 0) {
      const invalidFileNames = invalidFiles.map(file => file.name).join(', ');
      showToast(`Invalid file format(s): ${invalidFileNames}. Only PNG, JPG, and JPEG files are allowed.`, "error");
      return;
    }

    // Process files and add to uploadingImages state
    const processFiles = async () => {
      const newImages = [];

      for (const file of files) {
        try {
          const base64 = await convertToBase64(file);
          newImages.push({
            file,
            base64,
            name: file.name,
            status: 'pending', // pending, uploading, uploaded, failed
            error: null
          });
        } catch (error) {
          console.error('Error converting image to base64:', error);
          newImages.push({
            file,
            base64: null,
            name: file.name,
            status: 'failed',
            error: 'Failed to process image'
          });
        }
      }

      setUploadingImages(prev => [...prev, ...newImages]);
    };

    processFiles();
  };

  // Upload single image
  const uploadSingleImage = async (imageData, index) => {
    setUploadingImages(prev =>
      prev.map((img, idx) =>
        idx === index ? { ...img, status: 'uploading', error: null } : img
      )
    );

    try {
      const uploadData = {
        product_id: createdProduct?._id,
        color_id: selectedColorForUpload?._id,
        image: imageData.base64
      };

      const response = await uploadImageAPI(uploadData);

      if (response?.status === 200) {
        setUploadingImages(prev =>
          prev.map((img, idx) =>
            idx === index ? { ...img, status: 'uploaded', ...response.data } : img
          )
        );

        // Add to main images array
        setImages(prev => [...prev, response.data]);
        setImageColors(prev => [...prev, selectedColorForUpload.hex_code]);

        return true;
      } else {
        throw new Error(response?.message || 'Upload failed');
      }
    } catch (error) {
      setUploadingImages(prev =>
        prev.map((img, idx) =>
          idx === index ? { ...img, status: 'failed', error: `Failed to upload ${imageData.name}` } : img
        )
      );
      return false;
    }
  };

  // Start uploading all pending images
  const startUploading = async () => {
    const pendingImages = uploadingImages.filter(img => img.status === 'pending');

    for (let i = 0; i < uploadingImages.length; i++) {
      if (uploadingImages[i].status === 'pending') {
        const success = await uploadSingleImage(uploadingImages[i], i);
        if (!success) {
          // Stop on first failure
          break;
        }
      }
    }
  };

  // Retry failed upload and continue with remaining
  const retryUpload = async (index) => {
    const success = await uploadSingleImage(uploadingImages[index], index);

    if (success) {
      // Continue with remaining pending images after this index
      for (let i = index + 1; i < uploadingImages.length; i++) {
        if (uploadingImages[i].status === 'pending') {
          const nextSuccess = await uploadSingleImage(uploadingImages[i], i);
          if (!nextSuccess) {
            // Stop if next upload fails
            break;
          }
        }
      }
    }
  };

  // Remove image from uploading list
  const removeUploadingImage = (index) => {
    setUploadingImages(prev => prev.filter((_, idx) => idx !== index));
  };

  // Close modal
  const closeImageUploadModal = () => {
    setShowImageUploadModal(false);
    setSelectedColorForUpload(null);
    setUploadingImages([]);
  };

  const handleImageChange = (e) => {
    const files = Array?.from(e?.target?.files);

    // Validate file types
    const allowedTypes = ['image/png', 'image/jpg', 'image/jpeg'];
    const invalidFiles = files.filter(file => !allowedTypes.includes(file.type));

    if (invalidFiles.length > 0) {
      const invalidFileNames = invalidFiles.map(file => file.name).join(', ');
      showToast(`Invalid file format(s): ${invalidFileNames}. Only PNG, JPG, and JPEG files are allowed.`, "error");
      // Reset file input
      e.target.value = '';
      return;
    }

    const processFiles = async () => {
      const newImages = [];

      for (const file of files) {
        try {
          const base64 = await convertToBase64(file);
          newImages.push({
            file,
            url: base64
          });
        } catch (error) {
          console.error('Error converting image to base64:', error);
          // Fallback to object URL if base64 conversion fails
          newImages.push({
            file,
            url: URL?.createObjectURL(file)
          });
        }
      }

      setImages(prev => [...prev, ...newImages]);
      // Assign the color based on which section was clicked
      setImageColors(prev => [...prev, ...new Array(newImages.length).fill(uploadColor)]);
      setUploadColor(""); // Reset upload color
    };

    processFiles();

    // Reset file input value to allow selecting the same file again
    e.target.value = '';
  };

  // Handle color selection for an image
  const handleImageColorChange = (idx, value) => {
    const updated = [...imageColors];
    updated[idx] = value;
    setImageColors(updated);
  };

  // Show image delete confirmation
  const handleRemoveImage = (idx) => {
    setImageToDelete(idx);
    setShowImageDeleteModal(true);
  };

  // Confirm image deletion
  const confirmDeleteImage = async () => {
    if (imageToDelete === null) return;

    const imageToRemove = images[imageToDelete];

    // If image has an _id, it's from the API, so delete it from server
    if (imageToRemove._id) {
      try {
        const response = await deleteImageAPI({ image_id: imageToRemove._id });

        if (response?.status === 200) {
          // Remove from local state
          const newImages = images.filter((_, i) => i !== imageToDelete);
          const newImageColors = imageColors.filter((_, i) => i !== imageToDelete);

          setImages(newImages);
          setImageColors(newImageColors);

          showToast("Image deleted successfully!", "success");
        } else {
          showToast(`Failed to delete image: ${response?.message}`, "error");
        }
      } catch (error) {
        showToast(`Failed to delete image: ${error.message}`, "error");
      }
    } else {
      // If no _id, just remove from local state
      const newImages = images.filter((_, i) => i !== imageToDelete);
      const newImageColors = imageColors.filter((_, i) => i !== imageToDelete);

      setImages(newImages);
      setImageColors(newImageColors);
      showToast("Image removed successfully!", "success");
    }

    // Close modal
    setShowImageDeleteModal(false);
    setImageToDelete(null);
  };

  // Cancel image deletion
  const cancelDeleteImage = () => {
    setShowImageDeleteModal(false);
    setImageToDelete(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Set loading state
    setSubmitLoading(true);

    // Validation checks
    if (!productName.trim()) {
      showToast("Please enter the product name.");
      setSubmitLoading(false);
      return;
    }
    if (categoryIds.length === 0) {
      showToast("Please select a category.");
      setSubmitLoading(false);
      return;
    }

    // Size validation - check for incomplete size entries
    const incompleteSizes = sizes.filter(row => {
      const hasSize = row.size?.trim();
      const hasWholesale = row.wholesale_price?.toString().trim();
      const hasRetail = row.retail_price?.toString().trim();
      const hasUnit = row.unit?.toString().trim();

      // Check if any field is filled but not all
      const filledFields = [hasSize, hasWholesale, hasRetail, hasUnit].filter(Boolean).length;
      return filledFields > 0 && filledFields < 4;
    });

    if (incompleteSizes.length > 0) {
      // Show common message for incomplete sizes
      showToast("Please complete all size fields");
      setSubmitLoading(false);
      return;
    }

    if (!sizes?.some(row => row.size && row.wholesale_price && row?.retail_price && row?.unit)) {
      showToast("Please add at least one size.");
      setSubmitLoading(false);
      return;
    }

    // Brand is optional - use default brand if not selected
    const selectedBrandId = BrandId || (brands.length > 0 ? brands[0]._id : null);
    if (!selectedBrandId) {
      showToast("No brands available. Please add a brand first.");
      setSubmitLoading(false);
      return;
    }

    // Color validation - check for incomplete color entries
    const colorsWithNameButNoHex = colors.filter(c => c.name?.trim() && !c.hex_code);
    const colorsWithHexButNoName = colors.filter(c => c.hex_code && !c.name?.trim());

    if (colorsWithNameButNoHex.length > 0) {
      const colorNames = colorsWithNameButNoHex.map(c => c.name).join(', ');
      showToast(`Please enter hex code for color ${colorNames}`);
      setSubmitLoading(false);
      return;
    }

    if (colorsWithHexButNoName.length > 0) {
      const hexCodes = colorsWithHexButNoName.map(c => c.hex_code).join(', ');
      showToast(`Please enter color name for hex code ${hexCodes}`);
      setSubmitLoading(false);
      return;
    }

    // Check if at least one complete color is added
    if (!colors.some(c => c.name && c.hex_code)) {
      showToast("Please add at least one color with name and hex code.");
      setSubmitLoading(false);
      return;
    }

    try {
      // Prepare product data
      const productData = {
        name: productName,
        description: description,
        is_featured: featured ? 1 : 0,
        return_policy: returnPolicy,
        fabric: fabric,
        category_id: categoryIds.map(id => parseInt(id)),
        brand_id: parseInt(selectedBrandId),
        colors: colors.filter(c => c.name && c.hex_code).map(c => ({
          name: c.name,
          hex_code: c.hex_code
        })),
        sizes: sizes.filter(row => row.size && row.wholesale_price && row.retail_price && row.unit).map(row => ({
          size: row.size,
          retail_price: parseInt(row.retail_price),
          wholesale_price: parseInt(row.wholesale_price),
          unit: parseInt(row.unit)
        })),
      };

      const response = await addProductAPI(productData);

      if (response?.status === 200) {
        setCreatedProduct(response.data);
        setProductCreated(true);
        showToast("Product added successfully!", "success");
        await new Promise(resolve => setTimeout(resolve, 2000));
        const url = catId
          ? `/product?categoryId=${catId}&refresh=true`
          : `/product?refresh=true`;
        navigate(url, { state: { filter } });

      } else if (response?.message) {
        showToast(typeof response?.message === 'string'
          ? response?.message
          : 'Failed due to internal error', "error");
      } else {
        showToast('Failed due to internal error', "error");
      }
    } catch (err) {
      showToast('Failed due to internal error', "error");
    } finally {
      setSubmitLoading(false);
    }
  };

  return (
    <>
      <Toast toast={toast} />
      <div className="product-main-content">
        <div className="order-details-header">
          <button className="back-button" onClick={() => {
            const url = catId
              ? `/product?categoryId=${catId}`
              : `/product`;
            navigate(url, { state: { filter } });
          }}>
            <FaArrowLeft /> Back to Products
          </button>
        </div>
        <form onSubmit={handleSubmit} className="product-form">
          <TextArea
            title="Product Name"
            value={productName}
            onChange={e => setProductName(e.target.value)}
            placeholder="Enter product name"
            required
            maxLength={255}
            rows={3}
          />

          <RichTextInput
            title="Description"
            value={description}
            onChange={setDescription}
            placeholder="Enter product description"
            rows={6}
          />

          <RichTextInput
            title="Return Policy"
            value={returnPolicy}
            onChange={setReturnPolicy}
            placeholder="Enter return policy..."
            rows={6}
          />

          <Input
            title="Fabric"
            type="text"
            value={fabric}
            onChange={e => setFabric(e.target.value)}
            placeholder="Enter fabric type (e.g. Cotton, Silk)"
            maxLength={255}
          />

          <div className="cat-list-section">
            <div className="cat-list-header">
              <h3 className="cat-list-title">Category</h3>
              <label className="cat-list-item cat-select-all">
                <input
                  type="checkbox"
                  checked={categories.length > 0 && categoryIds.length === categories.length}
                  onChange={() => {
                    if (categoryIds.length === categories.length) setCategoryIds([]);
                    else setCategoryIds(categories.map(c => String(c._id)));
                  }}
                />
                <span>Select All</span>
              </label>
            </div>
            {loadingCategories ? (
              <p className="cat-list-loading">Loading categories...</p>
            ) : (
              <div className="cat-list-grid">
                {categories.map(cat => {
                  const id = String(cat._id);
                  const checked = categoryIds.includes(id);
                  return (
                    <label key={id} className={`cat-list-item${checked ? ' cat-list-item--checked' : ''}`}>
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={() => setCategoryIds(prev =>
                          prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
                        )}
                      />
                      <span>{cat.name}</span>
                    </label>
                  );
                })}
              </div>
            )}
          </div>

          <Select
            title="Brand"
            value={BrandId}
            onChange={e => setBrandId(e.target.value)}
            placeholder={loadingBrands ? "Loading brands..." : "Select Brand"}
            disabled={loadingBrands}
            options={brands.map(brand => ({ value: brand._id, label: brand.name }))}
          />

          <Checkbox
            title="Featured"
            checked={featured}
            onChange={e => setFeatured(e.target.checked)}
          />

          <div className="size-section">
            <h3 className="size-title">Size</h3>
            <div className="size-header">
              <div className="size-header-item">SIZE</div>
              <div className="size-header-item">WHOLESALE PRICE</div>
              <div className="size-header-item">RETAIL PRICE</div>
              <div className="size-header-item">UNIT</div>
              <div className="remove-price-header-placeholder"></div>
            </div>
            {sizes.map((row, idx) => (
              <div className="price-row" key={idx}>
                <input
                  type="text"
                  className="size-textarea"
                  placeholder="Size"
                  value={row.size}
                  onChange={e => handleSizeChange(idx, "size", e.target.value)}
                />
                <input
                  type="text"
                  className="price-input"
                  placeholder="Wholesale Price"
                  value={row.wholesale_price}
                  onChange={e => handleSizeChange(idx, "wholesale_price", e.target.value)}
                  maxLength={8}
                  inputMode="numeric"
                  pattern="[0-9]*"
                />
                <input
                  type="text"
                  className="price-input"
                  placeholder="Retail Price"
                  value={row.retail_price}
                  onChange={e => handleSizeChange(idx, "retail_price", e.target.value)}
                  maxLength={8}
                  inputMode="numeric"
                  pattern="[0-9]*"
                />
                <input
                  type="text"
                  className="price-input"
                  placeholder="Unit"
                  value={row.unit}
                  onChange={e => handleSizeChange(idx, "unit", e.target.value)}
                  maxLength={8}
                  inputMode="numeric"
                  pattern="[0-9]*"
                />
                {sizes.length > 1 ? (
                  <button
                    type="button"
                    className="remove-price-btn"
                    onClick={() => handleRemoveSizeRow(idx)}
                    title="Delete"
                  >
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
                      strokeLinecap="round" strokeLinejoin="round">
                      <polyline points="3 6 5 6 21 6"></polyline>
                      <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v2"></path>
                      <line x1="10" y1="11" x2="10" y2="17"></line>
                      <line x1="14" y1="11" x2="14" y2="17"></line>
                    </svg>
                  </button>
                ) : (
                  <div className="remove-price-header-placeholder"></div>
                )}
              </div>
            ))}
            {/* {!productCreated && ( */}
            <div className="add-price-btn-container">
              <button
                type="button"
                className="add-price-btn"
                onClick={handleAddSizeRow}
                title="Add Size"
              >
                Add More
              </button>
            </div>
            {/* )} */}
          </div>

          {/* Color Section */}
          <div className="size-section">
            <h3 className="size-title">Color</h3>
            <div className="size-header">
              <div className="size-header-item">COLOR NAME</div>
              <div className="size-header-item">COLOR PICKER</div>
              <div className="size-header-item">HEX CODE</div>
              <div className="remove-size-header-placeholder"></div>
            </div>
            {colors.map((color, idx) => (
              <div className="price-row" key={idx}>
                <input
                  type="text"
                  placeholder="Color name"
                  value={color.name}
                  onChange={e => handleColorChange(idx, "name", e.target.value)}
                  className="size-textarea"
                  disabled={color.isNoColor}
                />
                <input
                  type="color"
                  value={color.hex_code === "rgba(0, 0, 0, 0)" ? "#000000" : color.hex_code || "#000000"}
                  onChange={e => handleColorChange(idx, "hex_code", e.target.value)}
                  className="color-picker-input"
                  disabled={color.isNoColor}
                />
                <input
                  type="text"
                  placeholder="Hex code"
                  value={color.hex_code}
                  onChange={e => handleColorChange(idx, "hex_code", e.target.value)}
                  className="size-textarea"
                  maxLength={7}
                  disabled={color.isNoColor}
                />
                {!color.isNoColor && colors.length > 1 ? (
                  <button
                    type="button"
                    className="remove-price-btn"
                    onClick={() => handleRemoveColor(idx)}
                    title="Delete"
                  >
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
                      strokeLinecap="round" strokeLinejoin="round">
                      <polyline points="3 6 5 6 21 6"></polyline>
                      <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v2"></path>
                      <line x1="10" y1="11" x2="10" y2="17"></line>
                      <line x1="14" y1="11" x2="14" y2="17"></line>
                    </svg>
                  </button>
                ) : (
                  <div className="remove-color-btn-placeholder"></div>
                )}
              </div>
            ))}
            {/* {!productCreated && ( */}
            <div className="add-size-btn-container">
              <button
                type="button"
                onClick={handleAddMoreColor}
                className="add-price-btn"
                title="Add Color"
              >
                Add More
              </button>
            </div>
            {/* )} */}
          </div>

          {/* Submit Button - Show only before product is created */}
          {/* {!productCreated && ( */}
          <div className="submit-btn-row">
            <button
              type="submit"
              className="submit-btn"
              disabled={submitLoading}
            >
              {submitLoading ? "Submitting..." : "Submit"}
            </button>
          </div>
          {/* )} */}

          {/* Image Section - Show only after product is created */}
          {/* {productCreated && (
          <div className="size-section">
            <h3 className="size-title">Add Images</h3>

            {createdProduct && createdProduct.colors && createdProduct.colors.map((color) => {
                const colorImages = images.filter((img, idx) => {
                  const imageColor = imageColors[idx] || "";
                  return imageColor === color.hex_code;
                });
                return (
                  <div className="color-row" key={color._id} style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'flex-start',
                    gap: '5px'
                  }}>
                    <div className="color-label" style={{
                      fontWeight: 'bold',
                      color: '#333',
                      minWidth: '80px'
                    }}>
                      {color.name}
                    </div>
                    <div className="image-boxes-container" style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                      {images.map((img, idx) => {
                        const imageColor = imageColors[idx] || "";
                        if (imageColor === color.hex_code) {
                          return (
                            <div className="image-preview-item" key={idx}>
                              <img src={img.image || img.url} alt={`preview-${idx}`} className="image-preview" />
                              <button
                                type="button"
                                className="remove-image-btn"
                                onClick={() => handleRemoveImage(idx)}
                              >
                                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
                                  strokeLinecap="round" strokeLinejoin="round">
                                  <polyline points="3 6 5 6 21 6"></polyline>
                                  <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v2"></path>
                                  <line x1="10" y1="11" x2="10" y2="17"></line>
                                  <line x1="14" y1="11" x2="14" y2="17"></line>
                                </svg>
                              </button>
                            </div>
                          );
                        }
                        return null;
                      })}

                      <div className="image-upload-box" onClick={() => handleImageUpload(color.hex_code)}>
                        <span className="plus-icon">+</span>
                      </div>
                    </div>
                  </div>
                );
              })}
            <input
              id="image-upload-input"
              type="file"
              accept=".png,.jpg,.jpeg,image/png,image/jpg,image/jpeg"
              multiple
              onChange={handleImageChange}
              className="image-input"
              style={{ display: "none" }}
            />
          </div>
          )} */}

        </form>

        {/* Delete Confirmation Modal */}
        <ConfirmationModal
          visible={showDeleteModal}
          onConfirm={confirmDeleteColor}
          onCancel={cancelDeleteColor}
          title="Delete Color"
          message={`This color has images. Do you want to delete the color and its images?`}
          confirmText="Delete"
        />

        {/* Image Delete Confirmation Modal */}
        <ConfirmationModal
          visible={showImageDeleteModal}
          onConfirm={confirmDeleteImage}
          onCancel={cancelDeleteImage}
          title="Delete Image"
          message="Are you sure you want to delete this image?"
          confirmText="Delete"
        />

        {/* Image Upload Modal */}
        <ImageUploadModal
          show={showImageUploadModal}
          onClose={closeImageUploadModal}
          selectedColor={selectedColorForUpload}
          uploadingImages={uploadingImages}
          onFileChange={handleModalImageChange}
          onStartUploading={startUploading}
          onRetryUpload={retryUpload}
          onRemoveImage={removeUploadingImage}
        />
      </div>
    </>
  );
};

export default AddProduct; 